import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { marketingApi } from '../api/marketing'
import Boton from '../components/Boton'
import { useAuth } from '../context/AuthContext'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useStoredState } from '../hooks/useStoredState'
import { fmtFecha } from '../utils/fecha'
import { colors, font, text, space, radius, sx } from '../design/tokens'

/**
 * MARKETING → Segunda compra (Martin, 17 oct 2026 — docs/diseno-segunda-compra.md):
 * a cada cliente que hizo su primera compra le llegan tres mails (días 25, 45 y
 * 75) que se cortan si vuelve a comprar. 1 de cada 10 es grupo de control (no
 * recibe nada) para medir cuánto aporta. Nace apagada: se prende acá.
 */

const VISTAS = [
  { id: '', label: 'Todos' },
  { id: 'activa', label: 'En curso' },
  { id: 'volvio', label: 'Volvieron' },
  { id: 'terminada', label: 'Terminaron' },
  { id: 'cortada', label: 'Cortados' },
  { id: 'control', label: 'Grupo de control' },
]

const ESTADO = {
  activa: { label: 'En curso', bg: colors.infoBg, fg: colors.info },
  volvio: { label: 'Volvió a comprar', bg: colors.okBg, fg: colors.ok },
  terminada: { label: 'Terminó sin volver', bg: colors.bgSoft, fg: colors.textSub },
  cortada: { label: 'Cortado', bg: colors.warnBg, fg: colors.warn },
}

const pct = (v) => (v == null ? '—' : `${String(v).replace('.', ',')} %`)

export default function MktSegundaCompraPage() {
  const { hasAnyRole } = useAuth()
  const esAdmin = hasAnyRole(['administrador'])
  const [vista, setVista] = useStoredState('lgr.filtros.segunda-compra', '')
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)
  const [aviso, setAviso] = useState(null)
  const [ocupado, setOcupado] = useState(false)
  const [confirmar, setConfirmar] = useState(false)

  const cargar = useCallback(async () => {
    try {
      const r = await marketingApi.segundaCompra({ estado: vista || undefined })
      setData(r.data)
      setError(false)
    } catch { setError(true) }
  }, [vista])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(cargar, 60000)

  const r = data?.resumen
  const activa = !!r?.activo_desde

  const muestra = async () => {
    setOcupado(true); setAviso(null)
    try {
      const { data: d } = await marketingApi.segundaCompraMuestra()
      setAviso({ ok: true, texto: d.mensaje })
    } catch (e) {
      setAviso({ ok: false, texto: e.response?.data?.message || 'No se pudo armar la muestra.' })
    } finally { setOcupado(false) }
  }

  const cambiarEstado = async () => {
    setOcupado(true); setAviso(null)
    try {
      await marketingApi.segundaCompraActivar(!activa)
      setConfirmar(false)
      setAviso({ ok: true, texto: activa ? 'La Segunda compra quedó apagada: no sale ningún mail más.' : 'La Segunda compra quedó prendida: los mails salen de 18 a 21 h.' })
      cargar()
    } catch (e) {
      setAviso({ ok: false, texto: e.response?.data?.message || 'No se pudo cambiar.' })
    } finally { setOcupado(false) }
  }

  const filas = data?.data ?? []

  return (
    <div>
      <div style={st.header}>
        <div>
          <h2 style={st.title}>Segunda compra</h2>
          <p style={st.sub}>
            Clientes que hicieron su primera compra. Reciben tres mails con una selección pensada para ellos
            (días 25, 45 y 75), que se cortan si vuelven a comprar. 1 de cada 10 no recibe nada: es el grupo de control.
          </p>
        </div>
        <div style={st.acciones}>
          <span style={{ ...sx.badge, ...(activa ? { background: colors.okBg, color: colors.ok } : { background: colors.bgSoft, color: colors.textSub }) }}>
            {activa ? `Prendida desde ${fmtFecha(r.activo_desde)}` : 'Apagada'}
          </span>
          <Boton variante="secondary" onClick={muestra} disabled={ocupado} title="Te manda a tu mail tres mails reales (uno de cada etapa). Al cliente no le llega nada.">
            Mandarme una muestra
          </Boton>
          {esAdmin && !confirmar && (
            <Boton variante={activa ? 'secondary' : 'primary'} onClick={() => setConfirmar(true)} disabled={ocupado}>
              {activa ? 'Apagar' : 'Prender'}
            </Boton>
          )}
        </div>
      </div>

      {confirmar && (
        <div style={st.confirmar}>
          <span>
            {activa
              ? '¿Apagar la Segunda compra? No sale ningún mail más hasta que la vuelvas a prender.'
              : '¿Prender la Segunda compra? Se inscriben los clientes nuevos cuya primera compra fue hace menos de 25 días y los mails empiezan a salir de 18 a 21 h.'}
          </span>
          <Boton variante="primary" sm onClick={cambiarEstado} disabled={ocupado}>{activa ? 'Sí, apagar' : 'Sí, prender'}</Boton>
          <Boton variante="ghost" sm onClick={() => setConfirmar(false)}>Cancelar</Boton>
        </div>
      )}
      {aviso && <p style={{ ...st.aviso, color: aviso.ok ? colors.ok : colors.err }}>{aviso.texto}</p>}

      <div style={st.comparacion}>
        <Grupo titulo="Con mails" g={r?.con_mails} destacado />
        <Grupo titulo="Sin mails (control)" g={r?.control} />
        <div style={st.mails}>
          <div style={st.kpiLabel}>Por mail</div>
          <table style={{ ...sx.table, marginTop: space.xs }}>
            <thead>
              <tr>
                <th style={st.thChico}>Mail</th><th style={st.thChico}>Enviados</th><th style={st.thChico}>Abrieron</th>
                <th style={st.thChico}>Entraron</th><th style={st.thChico}>Compraron por el link</th>
              </tr>
            </thead>
            <tbody>
              {(r?.por_mail ?? []).map(m => (
                <tr key={m.etapa}>
                  <td style={st.tdChico}>{m.etapa} · día {[25, 45, 75][m.etapa - 1]}</td>
                  <td style={st.tdChico}><span style={sx.mono}>{m.enviados}</span></td>
                  <td style={st.tdChico}><span style={sx.mono}>{m.abiertos}</span></td>
                  <td style={st.tdChico}><span style={sx.mono}>{m.entraron}</span></td>
                  <td style={st.tdChico}><span style={sx.mono}>{m.compraron_por_link}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={sx.filterBar}>
        {VISTAS.map(v => (
          <button key={v.id} type="button" onClick={() => setVista(v.id)}
            style={{ ...sx.pill, ...(vista === v.id ? sx.pillActive : {}) }}>{v.label}</button>
        ))}
      </div>

      {error && <p style={st.error}>No se pudo actualizar el listado. Se reintenta solo en un minuto.</p>}

      <div style={st.tableWrap}>
        <table style={sx.table}>
          <thead>
            <tr>
              <th style={sx.th}>Primera compra</th>
              <th style={sx.th}>Cliente</th>
              <th style={sx.th}>Pedido</th>
              <th style={sx.th}>Mails</th>
              <th style={sx.th}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {!data && !error && <tr><td colSpan={5} style={st.vacio}>Cargando…</td></tr>}
            {data && filas.length === 0 && (
              <tr><td colSpan={5} style={st.vacio}>
                {activa ? 'Todavía no hay clientes en este filtro.' : 'La Segunda compra está apagada. Mandate una muestra para ver los mails y prendela cuando quieras.'}
              </td></tr>
            )}
            {filas.map(f => (
              <tr key={f.id} style={st.fila}>
                <td style={st.td}><span style={sx.mono}>{fmtFecha(f.primera_compra)}</span></td>
                <td style={st.td}>
                  <div style={st.nombre}>
                    {f.cliente.nombre || 'Sin nombre en la ficha'}
                    {f.grupo_control && <span style={st.control} title="Grupo de control: no recibe mails, se mide igual">control</span>}
                  </div>
                  <div style={st.subLinea}>{f.email}</div>
                </td>
                <td style={st.td}>
                  {f.nv ? <Link to={`/notas-venta/${f.nv.id}`} style={st.link}>{f.nv.numero}</Link> : '—'}
                </td>
                <td style={st.td}><Mails f={f} /></td>
                <td style={st.td}>
                  <span style={{ ...sx.badge, background: (ESTADO[f.estado] ?? ESTADO.activa).bg, color: (ESTADO[f.estado] ?? ESTADO.activa).fg }}>
                    {(ESTADO[f.estado] ?? ESTADO.activa).label}
                  </span>
                  {f.volvio_por && <div style={st.subLinea}>{f.volvio_por}</div>}
                  {f.motivo && f.estado !== 'volvio' && <div style={st.subLinea}>{f.motivo}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Grupo({ titulo, g, destacado }) {
  return (
    <div style={{ ...st.kpi, ...(destacado ? st.kpiDestacado : {}) }}>
      <div style={st.kpiLabel}>{titulo}</div>
      <div style={{ ...st.kpiValor, color: destacado ? colors.ok : colors.text }}>
        {g?.inscriptos ? pct(g.pct_hoy) : '—'}<span style={st.kpiPct}>volvieron ({g?.volvieron ?? 0} de {g?.inscriptos ?? 0})</span>
      </div>
      <div style={st.subLinea}>
        {g?.con_90_dias
          ? `A los 90 días: ${pct(g.pct_90_dias)} (sobre ${g.con_90_dias} con 90 días cumplidos)`
          : 'A los 90 días: todavía nadie cumplió 90 días'}
      </div>
    </div>
  )
}

function Mails({ f }) {
  if (f.grupo_control) return <span style={st.nada}>no recibe</span>
  if (f.mails.length === 0) return <span style={st.nada}>el 1º sale el día 25</span>
  return (
    <div style={{ display: 'flex', gap: space.md }}>
      {f.mails.map(m => (
        <span key={`${m.etapa}-${m.enviado_at}`} style={{ ...st.paso, color: m.estado === 'failed' ? colors.err : colors.text }}
          title={m.error || `Mail ${m.etapa}${m.enviado_at ? ' · enviado ' + fmtFecha(m.enviado_at) : ''}`}>
          {m.etapa}: {m.estado === 'failed' ? 'no salió' : <>
            <span style={{ color: m.abierto_at ? colors.ok : colors.textMeta }}>{m.abierto_at ? '✓' : '○'} abrió</span>{' '}
            <span style={{ color: m.click_at ? colors.ok : colors.textMeta }}>{m.click_at ? '✓' : '○'} entró</span>
            {m.compra && <span style={{ color: colors.ok }}> · compró {m.compra}</span>}
          </>}
        </span>
      ))}
    </div>
  )
}

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.lg, marginBottom: space.lg, flexWrap: 'wrap' },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: `${space.xs}px 0 0`, fontSize: text.sec, color: colors.textSec, maxWidth: 720 },
  acciones: { display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap' },
  confirmar: { display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap', padding: `${space.md}px ${space.lg}px`, background: colors.warnBg, border: `1px solid ${colors.border}`, borderRadius: radius.md, marginBottom: space.md, fontSize: text.body },
  aviso: { fontSize: text.sec, margin: `0 0 ${space.md}px` },
  comparacion: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: space.md, marginBottom: space.lg },
  kpi: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px` },
  kpiDestacado: { borderColor: colors.ok, background: colors.okSoft },
  kpiLabel: { fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  kpiValor: { ...sx.mono, fontSize: 20, fontWeight: 600, marginTop: space.xs },
  kpiPct: { fontFamily: font.sans, fontSize: text.sec, fontWeight: 400, color: colors.textSec, marginLeft: space.sm },
  mails: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, gridColumn: 'span 2', minWidth: 0, overflowX: 'auto' },
  thChico: { ...sx.th, height: 28, fontSize: text.meta },
  tdChico: { ...sx.td, height: 28, fontSize: text.sec, whiteSpace: 'nowrap' },
  error: { fontSize: text.sec, color: colors.err, margin: `0 0 ${space.md}px` },
  tableWrap: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, overflowX: 'auto' },
  fila: { height: 52, borderBottom: `1px solid ${colors.borderSoft}` },
  vacio: { ...sx.td, height: 64, textAlign: 'center', color: colors.textSec },
  td: { ...sx.td, padding: `0 ${space.lg}px`, whiteSpace: 'nowrap' },
  nombre: { fontWeight: 500 },
  control: { ...sx.badge, marginLeft: space.sm, background: colors.bgSoft, color: colors.textSec, border: `1px solid ${colors.border}` },
  subLinea: { fontSize: text.sec, color: colors.textMeta, marginTop: 2 },
  link: { ...sx.mono, color: colors.link, textDecoration: 'none', fontWeight: 500, whiteSpace: 'nowrap' },
  nada: { color: colors.textMeta, fontSize: text.sec },
  paso: { fontSize: text.sec, fontWeight: 500, whiteSpace: 'nowrap' },
}
